import type { CifraPanel, PanelInicio, TarjetaHoy } from '../../api/ops/analitica';
import type { CifrasIndicador } from '../../api/ops/indicadores';
import type { AccesoDeInicio, Aviso, Contador } from '../../api/operaciones';
import { texto } from '../indicadores/reglas';

/**
 * Reglas de Inicio (7.1) sin estilos. «Pantalla de calma»: arriba una frase positiva con el nombre del negocio, después
 * las funciones de Reservaria y «Tus accesos»; las cifras, de lo positivo a lo negativo; avisos y consumo, al final. El
 * panel no tiene códigos de indicador ni listas por tipo de negocio: todo sale de la API (cada tarjeta solo si llega su
 * cifra, G521).
 */

/** Frase del título: la mejor subida de lo que la persona puede leer; si no hay ninguna, una frase de hoy (nunca negativa). */
export interface FraseTitulo {
    antes: string;
    dato: string;
    despues: string;
    contexto: string;
}

const enMinuscula = (s: string): string => s.charAt(0).toLocaleLowerCase('es') + s.slice(1);

/**
 * Si que suba es bueno, por código de indicador (`higher_is_better` de `IndicatorSummary`, G826); null si no se sabe. Inicio
 * lo saca del catálogo (`listIndicators`), nunca de los manifiestos.
 */
export type SubeBien = (codigo: string) => boolean | null;
const sinSaber: SubeBien = () => null;

/** Cifras del periodo que suben y es bueno que suban (G826: unos no-shows que suben no son noticia), de más a menos. */
export function subidas(panel: PanelInicio | null, subeBien: SubeBien = sinSaber): CifraPanel[] {
    if (panel === null) {
        return [];
    }
    return panel.cards
        .flatMap((c) => c.items)
        .filter((i) => i.complete && typeof i.change_percent === 'number' && i.change_percent > 0 && subeBien(i.indicator) !== false)
        .sort((a, b) => (b.change_percent ?? 0) - (a.change_percent ?? 0));
}

export function fraseDelTitulo(nombre: string, panel: PanelInicio | null, hoy: TarjetaHoy | null, subeBien: SubeBien = sinSaber): FraseTitulo {
    const mejor = subidas(panel, subeBien)[0];
    if (mejor !== undefined) {
        const actual = mejor.current.value;
        const anterior = mejor.previous?.value;
        // En recuentos, la diferencia («25 reservas más»); en lo demás, el porcentaje.
        if ((mejor.unit ?? 'count') === 'count' && typeof actual === 'number' && typeof anterior === 'number' && actual > anterior) {
            return { antes: `${nombre}, esta semana llevas `, dato: `${String(actual - anterior)} ${enMinuscula(mejor.label)}`, despues: ' más.', contexto: 'Últimos 7 días frente a los 7 anteriores' };
        }
        return {
            antes: `${nombre}, ${enMinuscula(mejor.label)} `,
            dato: `sube un ${String(Math.round(mejor.change_percent ?? 0))} %`,
            despues: ' esta semana.',
            contexto: 'Últimos 7 días frente a los 7 anteriores',
        };
    }
    const deHoy = hoy?.items.find((i) => i.value > 0);
    if (deHoy !== undefined) {
        return { antes: `${nombre}, hoy llevas `, dato: `${String(deHoy.value)} · ${enMinuscula(deHoy.label)}`, despues: '.', contexto: 'Hoy' };
    }
    return { antes: `${nombre}, `, dato: 'todo listo', despues: ' para hoy.', contexto: 'Hoy' };
}

/** Cuántos botones «Lo que Reservaria hace por ti» caben (7.1: «por su orden y hasta 6»). */
export const MAX_FUNCIONES = 6;

/**
 * Botones «Lo que Reservaria hace por ti» (G800): los `feature` que manda el servidor (ya filtrados por bloque contratado y
 * por lo que el rol ve), por su orden y hasta 6 (7.1).
 */
export function funciones(accesos: readonly AccesoDeInicio[]): AccesoDeInicio[] {
    return accesos
        .filter((a) => a.kind === 'feature')
        .sort((a, b) => a.order - b.order || a.key.localeCompare(b.key))
        .slice(0, MAX_FUNCIONES);
}

/** Indicadores distintos que piden los botones para su dato vivo. */
export function indicadoresDeFunciones(accesos: readonly AccesoDeInicio[]): string[] {
    // Sin el bloque (G963), el botón va sin dato.
    return [...new Set(funciones(accesos).flatMap((a) => (a.indicator_code && !a.not_contracted ? [a.indicator_code] : [])))];
}

/** Dato vivo de un botón: el total de su indicador en los últimos 7 días, en su unidad («12 esta semana»); sin cifra, nada. */
export function datoVivo(cifras: CifrasIndicador | undefined): string | null {
    const total = cifras?.series[0]?.total;
    if (total === undefined || (total.value === null && total.money === null) || total.value === 0) {
        return null;
    }
    return `${texto(cifras?.unit, total)} esta semana`;
}

/** Horas reservadas de cada profesional (G799): minutos del periodo por profesional, en horas, de más a menos. Sin %. */
export function horasPorProfesional(cifras: CifrasIndicador | null): { id: string; nombre: string; horas: string }[] {
    if (cifras === null) {
        return [];
    }
    return cifras.series
        .filter((s) => s.dimension_value !== null && s.dimension_value !== undefined && (s.total.value ?? 0) > 0)
        .sort((a, b) => (b.total.value ?? 0) - (a.total.value ?? 0))
        .map((s) => ({
            id: s.dimension_value ?? '',
            nombre: s.label ?? 'Profesional',
            horas: `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 1 }).format((s.total.value ?? 0) / 60)} h`,
        }));
}

/** Cambio frente al periodo anterior, como lo da la API: «+12 %», «−3 %» o nada. */
export function textoCambio(item: Pick<CifraPanel, 'change_percent'>): { texto: string; tono: 'sube' | 'baja' | 'igual' } | null {
    const c = item.change_percent;
    if (typeof c !== 'number') {
        return null;
    }
    const redondo = Math.round(c);
    if (redondo === 0) {
        return { texto: '= que antes', tono: 'igual' };
    }
    return { texto: `${redondo > 0 ? '+' : '−'}${String(Math.abs(redondo))} %`, tono: redondo > 0 ? 'sube' : 'baja' };
}

/** «Esta semana»: lo que sube primero, después lo que se queda igual o no se compara y al final lo que baja. */
export function ordenarDeBuenoAMalo(items: readonly CifraPanel[], subeBien: SubeBien = sinSaber): CifraPanel[] {
    // Lo bueno es subir si `higher_is_better` (G826) y bajar si no; sin saberlo, subir.
    const peso = (i: CifraPanel): number => {
        if (typeof i.change_percent !== 'number' || i.change_percent === 0) {
            return 1;
        }
        return (i.change_percent > 0) === (subeBien(i.indicator) !== false) ? 0 : 2;
    };
    return [...items].sort((a, b) => peso(a) - peso(b) || (b.change_percent ?? 0) - (a.change_percent ?? 0));
}

const CONCEPTOS: Readonly<Record<string, string>> = {
    conversations: 'Conversaciones del asistente',
    sends: 'Envíos de campañas',
    whatsapp: 'Mensajes de WhatsApp',
    whatsapp_marketing_plan: 'Mensajes de marketing',
    voice_minutes: 'Minutos de llamadas',
    signatures: 'Firmas',
    professionals: 'Profesionales',
};

/** Consumo del plan (`getUsage`, solo con `plan.ver`): cada concepto con algo incluido, con su % gastado. */
export function lineasDeConsumo(contadores: readonly Contador[]): { concepto: string; texto: string; porcentaje: number }[] {
    return contadores
        .filter((c) => c.included > 0)
        .map((c) => ({
            concepto: CONCEPTOS[c.concept] ?? c.concept,
            texto: `${String(c.used)} de ${String(c.included + c.extra)}`,
            porcentaje: Math.min(100, Math.round((c.used / (c.included + c.extra)) * 100)),
        }));
}

/** «Para revisar»: los avisos críticos y pendientes que siguen abiertos, los críticos primero (hasta 5). */
export function paraRevisar(avisos: readonly Aviso[]): Aviso[] {
    const peso = { critical: 0, warning: 1, info: 2 } as const;
    return avisos
        .filter((a) => a.severity !== 'info' && a.state !== 'archived' && a.status === 'open')
        .sort((a, b) => peso[a.severity] - peso[b.severity])
        .slice(0, 5);
}

/** Fecha del día en palabras («jueves, 30 de octubre»). */
export function fechaLarga(fecha: string): string {
    const [a, m, d] = fecha.split('-').map(Number);
    return new Intl.DateTimeFormat('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(a ?? 2000, (m ?? 1) - 1, d ?? 1));
}
